import React from 'react';
import { COLORS, FONTS, inputStyle, labelStyle, cardStyle, handleInputFocus, handleInputBlur, primaryButtonStyle, secondaryButtonStyle } from './signupStyles';
import { StepIndicator } from './StepIndicator';
import { TagInput } from './TagInput';
import { GENDER_OPTIONS, ETHNICITY_OPTIONS, COUNTRIES, US_STATES, CA_PROVINCES, LANGUAGE_OPTIONS, EDUCATION_OPTIONS, INCOME_OPTIONS } from './signupData';

const selectStyle = {
  ...inputStyle,
  appearance: 'none',
  backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'12\' height=\'8\' viewBox=\'0 0 12 8\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L6 6L11 1\' stroke=\'%23666\' stroke-width=\'2\' stroke-linecap=\'round\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 16px center'
};

const AREA_TYPES = [
  { value: 'Urban', label: 'Urban', icon: 'M3 21V8l4-4 4 4v13H3zm6 0V12l4-4 4 4v9h-8zm6 0V10l4-4 4 4v11h-8z' },
  { value: 'Suburban', label: 'Suburban', icon: 'M4 21V10l8-6 8 6v11H4zm4-7h2v2H8v-2zm4 0h2v2h-2v-2zm-4 4h2v2H8v-2z' },
  { value: 'Rural', label: 'Rural', icon: 'M12 3L2 12h3v8h6v-5h2v5h6v-8h3L12 3z' }
];

const AreaCard = ({ type, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(type.value)}
    style={{
      flex: 1, minWidth: '80px', padding: '12px 8px', backgroundColor: '#fff',
      border: selected ? `2px solid ${COLORS.salmon}` : `1.5px solid ${COLORS.border}`,
      borderRadius: '6px', cursor: 'pointer', textAlign: 'center',
      transition: 'all 0.2s', position: 'relative'
    }}
  >
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={selected ? COLORS.salmon : COLORS.muted}
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '4px' }}>
      <path d={type.icon} />
    </svg>
    <div style={{ fontSize: '13px', fontWeight: '600', color: selected ? COLORS.text : COLORS.muted }}>{type.label}</div>
  </button>
);

const getRegionOptions = (country) => {
  if (country === 'United States') return US_STATES;
  if (country === 'Canada') return CA_PROVINCES;
  return null;
};

const SectionLabel = ({ children }) => (
  <div style={{
    fontSize: '11px', fontWeight: '700', textTransform: 'uppercase',
    letterSpacing: '0.8px', color: COLORS.muted, marginBottom: '16px'
  }}>
    {children}
  </div>
);

const Divider = () => (
  <div style={{
    height: '1px', margin: '20px 0',
    background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.06), transparent)'
  }} />
);

export const StepAboutYou = ({ formData, onUpdate, onNext, onBack }) => {
  const canContinue =
    formData.genderIdentities.length > 0 &&
    formData.ethnicGroups.length > 0 &&
    formData.country &&
    formData.languageFluency &&
    formData.education &&
    formData.income &&
    formData.areaType;

  const regionOptions = getRegionOptions(formData.country);
  const showOtherLanguages = formData.languageFluency && formData.languageFluency !== 'I am fluent in only English';

  return (
    <div style={{
      minHeight: '80vh', backgroundColor: COLORS.bg, display: 'flex',
      alignItems: 'flex-start', justifyContent: 'center', padding: '80px 20px 40px', fontFamily: FONTS.system
    }}>
      <style>{`
        @media (max-width: 480px) {
          .signup-about-card { padding: 32px 24px !important; }
          .signup-location-row { flex-direction: column !important; }
        }
      `}</style>
      <div className="signup-about-card" style={{ ...cardStyle, marginTop: '24px' }}>
        <StepIndicator currentStep={1} totalSteps={4} stepLabels={['Personal', 'About You', 'Health', 'Done']} />
        <h2 style={{ fontFamily: FONTS.serif, fontSize: '24px', fontWeight: '700', color: COLORS.text, margin: '0 0 8px', textAlign: 'center' }}>
          About You
        </h2>
        <p style={{ fontSize: '15px', color: COLORS.muted, margin: '0 0 24px', textAlign: 'center' }}>
          This helps us match you with relevant research
        </p>

        <SectionLabel>Demographics</SectionLabel>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Gender identity*</label>
          <TagInput values={formData.genderIdentities} onChange={(v) => onUpdate({ genderIdentities: v })}
            suggestions={GENDER_OPTIONS} placeholder="Search and select..." />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Racial and ethnic group(s)*</label>
          <TagInput values={formData.ethnicGroups} onChange={(v) => onUpdate({ ethnicGroups: v })}
            suggestions={ETHNICITY_OPTIONS} placeholder="Search and select..." />
        </div>

        <div className="signup-location-row" style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Country*</label>
            <select value={formData.country} onChange={(e) => onUpdate({ country: e.target.value, state: '' })}
              style={selectStyle} onFocus={handleInputFocus} onBlur={handleInputBlur}>
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>City</label>
            <input type="text" value={formData.city} onChange={(e) => onUpdate({ city: e.target.value })}
              placeholder="Your city" style={inputStyle} onFocus={handleInputFocus} onBlur={handleInputBlur} />
          </div>
        </div>

        {regionOptions && (
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>State / Province</label>
            <select value={formData.state} onChange={(e) => onUpdate({ state: e.target.value })}
              style={selectStyle} onFocus={handleInputFocus} onBlur={handleInputBlur}>
              <option value="">Select...</option>
              {regionOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}

        <Divider />
        <SectionLabel>Background</SectionLabel>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Language fluency*</label>
          <select value={formData.languageFluency} onChange={(e) => onUpdate({ languageFluency: e.target.value })}
            style={selectStyle} onFocus={handleInputFocus} onBlur={handleInputBlur}>
            <option value="">Select...</option>
            {LANGUAGE_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>

        {showOtherLanguages && (
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>What other language(s) are you fluent in?</label>
            <input type="text" value={formData.otherLanguages} onChange={(e) => onUpdate({ otherLanguages: e.target.value })}
              placeholder="Other languages" style={inputStyle} onFocus={handleInputFocus} onBlur={handleInputBlur} />
          </div>
        )}

        <div className="signup-location-row" style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Education*</label>
            <select value={formData.education} onChange={(e) => onUpdate({ education: e.target.value })}
              style={selectStyle} onFocus={handleInputFocus} onBlur={handleInputBlur}>
              <option value="">Select...</option>
              {EDUCATION_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Household income*</label>
            <select value={formData.income} onChange={(e) => onUpdate({ income: e.target.value })}
              style={selectStyle} onFocus={handleInputFocus} onBlur={handleInputBlur}>
              <option value="">Select...</option>
              {INCOME_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>Where do you live?*</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {AREA_TYPES.map(t => (
              <AreaCard key={t.value} type={t} selected={formData.areaType === t.value}
                onSelect={(v) => onUpdate({ areaType: v })} />
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onBack} style={secondaryButtonStyle}>Back</button>
          <button onClick={onNext} disabled={!canContinue} style={{
            ...primaryButtonStyle,
            flex: 1,
            opacity: canContinue ? 1 : 0.4,
            cursor: canContinue ? 'pointer' : 'not-allowed'
          }}>Continue</button>
        </div>
      </div>
    </div>
  );
};
